import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Button } from 'primereact/button'
import { ProgressSpinner } from 'primereact/progressspinner'
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview'
import ExportMenu, { ExportFormat } from '../../../components/novarium/ExportMenu'
import NvTooltips from '../../../components/novarium/NvTooltips'
import { clearPersistedTableState } from '../../../components/summary/summaryTable'
import {
  HospitalizationGroupBy,
  HospitalizationGroupRowDTO,
  HospitalizationGroupsDTO,
  HospitalizationOptionsDTO,
} from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import useNotify from '../../../hooks/useNotify'
import {
  HospitalizationGrid,
  HospitalizationTableParams,
  exportHospitalizationGrid,
  exportHospitalizationSummary,
  getHospitalizationGroups,
  getHospitalizationOptions,
  isAccessDenied,
  isRequestCanceled,
} from '../../../services/HospitalizationSummaryController'
import useSectionData from '../../UrgencySummary/useSectionData'
import { filtersKey, NO_SELECT_FILTERS } from '../hospitalizationFilterModel'
import { HospitalizationMeasure } from '../hospitalizationEvolutionModel'
import {
  HOSPITALIZATION_TAB_GRID,
  HOSPITALIZATION_TAB_LABEL_ID,
  HOSPITALIZATION_TABS,
  HospitalizationTab,
} from '../hospitalizationTabs'
import useHospitalizationFilters from '../useHospitalizationFilters'
import useHospitalizationSummary from '../useHospitalizationSummary'
import HospitalizationEvolution from './HospitalizationEvolution'
import HospitalizationEvolutionsTable, { EVOLUTIONS_TABLE_ID } from './HospitalizationEvolutionsTable'
import HospitalizationFilters from './HospitalizationFilters'
import HospitalizationGroupedTable from './HospitalizationGroupedTable'
import HospitalizationIndicators from './HospitalizationIndicators'
import HospitalizationList, { CENSUS_TABLE_ID, LIST_TABLE_ID } from './HospitalizationList'

/** Las grillas guardan filtros y orden en el navegador (pueden tener nombres de pacientes): la pantalla abre y cierra sin ellos. */
const TABLE_IDS = [LIST_TABLE_ID, CENSUS_TABLE_ID, EVOLUTIONS_TABLE_ID]
const clearTables = () => TABLE_IDS.forEach(clearPersistedTableState)

/**
 * Indicadores de internación: los filtros al instante (período y «Más filtros»), los cuatro indicadores del período y,
 * debajo, las pestañas Evolución | Agrupado | Lista | Internados hoy | Evoluciones médicas (esta última, solo con el
 * permiso). El período y los filtros viajan en `filters` y son los mismos en todas las pestañas y en la exportación; la
 * pestaña, la medida de la evolución y el agrupamiento no se pierden al cambiar los filtros.
 */
export default function HospitalizationSummaryPage() {
  const intl = useIntl()
  const notify = useNotify()

  const filtersData = useHospitalizationFilters()
  const { filters, applied, pending, patchFilters } = filtersData
  const { summary, status, retry } = useHospitalizationSummary(applied)
  const appliedRef = useRef(applied)
  appliedRef.current = applied

  // Antes del primer render de las tablas: abren sin filtros ni orden de una visita anterior
  useState(clearTables)
  useEffect(() => clearTables, [])

  const [tab, setTab] = useState<HospitalizationTab>('evolution')
  const [measure, setMeasure] = useState<HospitalizationMeasure>('occupancy')
  const [groupBy, setGroupBy] = useState<HospitalizationGroupBy>('SECTOR')
  // Opciones de los combos y permiso de evoluciones: se piden una sola vez al abrir la pantalla
  const [options, setOptions] = useState<HospitalizationOptionsDTO | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    getHospitalizationOptions(controller.signal)
      .then(setOptions)
      .catch((error: unknown) => {
        // La pantalla sigue funcionando: los combos quedan sin opciones y sin pestaña de evoluciones
        if (!controller.signal.aborted && !isRequestCanceled(error) && !isAccessDenied(error)) {
          notify.error(intl.formatMessage({ id: 'HospitalizationSummaryOptionsError' }))
        }
      })
    return () => controller.abort()
    // Se pide una sola vez por apertura de la pantalla
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const tabs = useMemo<HospitalizationTab[]>(
    () => HOSPITALIZATION_TABS.filter((name) => name !== 'evolutions' || options?.canViewEvolutions === true),
    [options?.canViewEvolutions],
  )
  const shownTab: HospitalizationTab = tabs.includes(tab) ? tab : 'evolution'

  // Lo último que pidió cada grilla (filtros de pantalla, de columna, buscador y orden): es lo que ve el usuario
  const lastLoad = useRef<Partial<Record<HospitalizationGrid, HospitalizationTableParams>>>({})
  const onListLoad = useCallback((params: HospitalizationTableParams) => { lastLoad.current.list = params }, [])
  const onCensusLoad = useCallback((params: HospitalizationTableParams) => { lastLoad.current.census = params }, [])
  const onEvolutionsLoad = useCallback((params: HospitalizationTableParams) => { lastLoad.current.evolutions = params }, [])

  // El agrupado se pide recién al abrir su pestaña y de nuevo con cada cambio de filtros o de agrupación
  const summaryReady = summary !== null && status !== 'error'
  const groups = useSectionData<HospitalizationGroupsDTO>(
    summaryReady && shownTab === 'grouped' ? `${filtersKey(applied)}|groups|${groupBy}` : null,
    (signal) => getHospitalizationGroups(applied, groupBy, signal),
  )

  /**
   * «Exportar» en la pestaña de una grilla baja lo que ella muestra (los filtros de pantalla de ahora más los de columna,
   * el buscador y el orden de su última consulta); en el resto, el resumen con el agrupamiento elegido.
   */
  const exportCurrent = useCallback((format: ExportFormat) => {
    const grid: HospitalizationGrid | undefined = shownTab in HOSPITALIZATION_TAB_GRID
      ? HOSPITALIZATION_TAB_GRID[shownTab as keyof typeof HOSPITALIZATION_TAB_GRID]
      : undefined
    if (grid) return exportHospitalizationGrid(grid, format, { ...lastLoad.current[grid], filters: appliedRef.current })
    // Evolución: el resumen; Agrupado: el agrupado con el agrupamiento elegido
    return exportHospitalizationSummary(appliedRef.current, shownTab === 'grouped' ? groupBy : null, format)
  }, [shownTab, groupBy])

  const title = intl.formatMessage({ id: 'HospitalizationSummaryTitle' })

  if (status === 'denied') {
    return (
      <div className="nv-scope nv-page">
        <header className="nv-page-head">
          <h1 className="nv-h1">{title}</h1>
        </header>
        <p className="nv-note" role="status">
          <i className="pi pi-info-circle" aria-hidden="true" />
          {intl.formatMessage({ id: 'HospitalizationSummaryAccessDenied' })}
        </p>
      </div>
    )
  }

  // Los indicadores que se ven no son los de los filtros: se están consultando o el cambio espera el retardo
  const busy = status === 'loading' || pending
  const hasChips = filters.sectorId !== null || filters.admissionTypeId !== null || filters.financierId !== null || filters.includeNurseryBeds
  const isEmpty = summaryReady && summary.current.patientDays === 0 && summary.current.admissions === 0
    && summary.current.discharges === 0 && summary.inpatientsToday === 0
  const showContent = summaryReady && !isEmpty

  const exportDisabledReason = busy ? intl.formatMessage({ id: 'HospitalizationSummaryExportLoading' })
    : status === 'error' ? intl.formatMessage({ id: 'HospitalizationSummaryExportBlocked' })
      : isEmpty ? intl.formatMessage({ id: 'HospitalizationSummaryExportEmpty' })
        : undefined

  const drill = (by: 'FINANCIER' | 'ADMISSION_TYPE', row: HospitalizationGroupRowDTO) => {
    if (row.id === null) return
    patchFilters(by === 'FINANCIER' ? { financierId: row.id } : { admissionTypeId: row.id })
    setTab('list')
  }

  return (
    <div className="nv-scope nv-page nv-hosp-page">
      <NvTooltips />
      <div className="nv-page-head">
        <div className="nv-head-row">
          <h1 className="nv-h1">{title}</h1>
          <ExportMenu
            fallbackFileName="indicadores-de-internacion"
            disabled={exportDisabledReason !== undefined}
            disabledReason={exportDisabledReason}
            onExport={exportCurrent}
          />
        </div>
      </div>

      <HospitalizationFilters data={filtersData} options={options} />

      {filtersData.rangeOrigin && (
        <div className="nv-hosp-range-back">
          <Button
            type="button"
            link
            icon="pi pi-angle-double-left"
            label={intl.formatMessage({ id: 'HospitalizationSummaryReturnTo' }, { period: filtersData.rangeOrigin.label })}
            onClick={filtersData.returnToPreviousRange}
          />
        </div>
      )}

      <div className="nv-hosp-results" aria-busy={busy}>
        {busy && (
          <div className="nv-hosp-loading" role="status">
            <div className="nv-loading nv-hosp-loading__box">
              <ProgressSpinner strokeWidth="4" style={{ width: 24, height: 24 }} />
              <span>{intl.formatMessage({ id: 'HospitalizationSummaryLoading' })}</span>
            </div>
          </div>
        )}

        {status === 'error' && !busy && (
          <div className="nv-card nv-hosp-error" role="alert">
            <p className="nv-note">
              <i className="pi pi-info-circle" aria-hidden="true" />
              {intl.formatMessage({ id: 'HospitalizationSummaryQueryError' })}
            </p>
            <Button type="button" className="buttonMain2" outlined label={intl.formatMessage({ id: 'HospitalizationSummaryRetry' })} onClick={retry} />
          </div>
        )}

        {isEmpty && (
          <div className={`nv-hosp-no-results${busy ? ' nv-hosp-blurred' : ''}`}>
            <p className="nv-muted">{intl.formatMessage({ id: 'HospitalizationSummaryEmpty' })}</p>
            {hasChips && (
              <Button
                type="button"
                link
                label={intl.formatMessage({ id: 'HospitalizationSummaryClearFilters' })}
                onClick={() => patchFilters(NO_SELECT_FILTERS)}
              />
            )}
          </div>
        )}

        {showContent && (
          <div className={busy ? 'nv-hosp-blurred' : undefined}>
            <HospitalizationIndicators summary={summary} />
          </div>
        )}
      </div>

      {showContent && (
        <TabView
          activeIndex={tabs.indexOf(shownTab)}
          onTabChange={(event: TabViewTabChangeEvent) => setTab(tabs[event.index])}
        >
          {tabs.map((name) => (
            <TabPanel key={name} header={intl.formatMessage({ id: HOSPITALIZATION_TAB_LABEL_ID[name] })}>
              {name === 'evolution' && (
                <div className={busy ? 'nv-hosp-blurred' : undefined}>
                  <HospitalizationEvolution
                    summary={summary}
                    filters={applied}
                    measure={measure}
                    onMeasureChange={setMeasure}
                    onViewDay={(date) => filtersData.viewRange(date, date)}
                  />
                </div>
              )}
              {name === 'grouped' && (
                <section className="nv-section" aria-label={intl.formatMessage({ id: 'HospitalizationSummaryTabGrouped' })}>
                  <HospitalizationGroupedTable
                    groupBy={groupBy}
                    onGroupByChange={setGroupBy}
                    groups={groups}
                    filters={applied}
                    onDrill={drill}
                  />
                </section>
              )}
              {name === 'list' && <HospitalizationList grid="list" filters={applied} options={options} onLoad={onListLoad} />}
              {name === 'census' && <HospitalizationList grid="census" filters={applied} options={options} onLoad={onCensusLoad} />}
              {name === 'evolutions' && <HospitalizationEvolutionsTable filters={applied} types={options?.evolutionTypes} onLoad={onEvolutionsLoad} />}
            </TabPanel>
          ))}
        </TabView>
      )}
    </div>
  )
}
